import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import CompanyHeader from "./FinancialS_Components/CompanyHeader";
import CashFlowTable from "./FinancialS_Components/CashFlowTable";
import "./CashFlowPage.scss";

const normalizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key.charAt(0).toLowerCase() + key.slice(1)] = normalizeKeys(nested);
      return acc;
    }, {});
  }
  return value;
};

const toNumber = (value) => Number(value ?? 0);

function computeCashFlow(cyBalance, pyBalance, cyIncome) {
  const cyB = cyBalance ?? {};
  const pyB = pyBalance ?? {};
  const cyI = cyIncome ?? {};

  const delta = (key) => toNumber(cyB[key]) - toNumber(pyB[key]);

  const operating = {
    netProfit: toNumber(cyI.fitimiNeto),
    changeReceivables: -delta("llogariteArketueshme"),
    changeInventory: -delta("stoqet"),
    changeOtherCurrentAssets: -delta("pasuriteTjeraAfatshkurtera"),
    changePayables: delta("llogaritePagueshmeTjera"),
    changeOtherCurrentLiabilities:
      delta("detyrimetTjeraAfatshkurtera") +
      delta("interesiPagueshme") +
      delta("tatimiFitimPagueshem"),
  };

  const netOperating =
    operating.netProfit +
    operating.changeReceivables +
    operating.changeInventory +
    operating.changeOtherCurrentAssets +
    operating.changePayables +
    operating.changeOtherCurrentLiabilities;

  const investing = {
    purchasePpe: -delta("pronaPaisjetImpiantet"),
    purchaseIntangibles: -delta("pasuritePaprekshme"),
    purchaseInvestmentProperty: -delta("pronaInvestuese"),
    purchaseFinancialInvestments:
      -(
        delta("investimetFinanciareAfatshkurtera") +
        delta("investimetFinanciareAfatgjata") +
        delta("investimetPjesmarrje") +
        delta("investimetFiliale") +
        delta("investimetNeVijim")
      ),
  };

  const netInvesting =
    investing.purchasePpe +
    investing.purchaseIntangibles +
    investing.purchaseInvestmentProperty +
    investing.purchaseFinancialInvestments;

  const financing = {
    changeShortTermLoans: delta("krediteHuateAfatshkurter"),
    changeLongTermLoans: delta("krediteHuateAfatgjata"),
    changeLeaseLiabilities:
      delta("detyrimetLizingutAfatshkurter") +
      delta("detyrimetLizingutAfatgjate"),
    changeOwnerCapital: delta("kapitaliPronarit"),
    changeReserves: delta("rezervatTjera"),
    changeRetainedEarnings: delta("fitimiBartur"),
    changeBankOverdraft: delta("mbiterheqjaBankare"),
  };

  const netFinancing =
    financing.changeShortTermLoans +
    financing.changeLongTermLoans +
    financing.changeLeaseLiabilities +
    financing.changeOwnerCapital +
    financing.changeReserves +
    financing.changeRetainedEarnings +
    financing.changeBankOverdraft;

  const openingCash = toNumber(pyB.paraja);
  const closingCash = toNumber(cyB.paraja);

  const netCashMovementRaw = netOperating + netInvesting + netFinancing;
  const netCashMovementFromBalance = closingCash - openingCash;

  const reconciliationAdjustment =
    netCashMovementFromBalance - netCashMovementRaw;

  const normalizedAdjustment =
    Math.abs(reconciliationAdjustment) < 0.005 ? 0 : reconciliationAdjustment;

  const netCashMovement = netCashMovementRaw + normalizedAdjustment;

  return {
    ...operating,
    netOperating,
    ...investing,
    netInvesting,
    ...financing,
    netFinancing,
    reconciliationAdjustment: normalizedAdjustment,
    openingCash,
    netCashMovement,
    closingCash,
  };
}

export default function CashFlowPage() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const [data, setData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

  const formatNumber = (value) => {
    const numericValue = Number(value ?? 0);
    const formatted = Math.abs(numericValue).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return numericValue < 0 ? `(${formatted})` : formatted;
  };

  const cfSections = [
    {
      title: "RRJEDHA NGA AKTIVITETET OPERATIVE",
      rows: [
        { key: "netProfit", label: "Fitimi neto i periudhës" },
        { key: "changeReceivables", label: "Ndryshimi në llogaritë e arkëtueshme" },
        { key: "changeInventory", label: "Ndryshimi në stoqe" },
        { key: "changeOtherCurrentAssets", label: "Ndryshimi në pasuritë e tjera afatshkurtra" },
        { key: "changePayables", label: "Ndryshimi në llogaritë e pagueshme" },
        { key: "changeOtherCurrentLiabilities", label: "Ndryshimi në detyrimet e tjera afatshkurtra" },
        { key: "netOperating", label: "Neto rrjedha nga aktivitetet operative", isTotal: true },
      ],
    },
    {
      title: "RRJEDHA NGA AKTIVITETET INVESTUESE",
      rows: [
        { key: "purchasePpe", label: "Investime në prona, pajisje dhe impiante" },
        { key: "purchaseIntangibles", label: "Investime në pasuri të paprekshme" },
        { key: "purchaseInvestmentProperty", label: "Investime në prona investuese" },
        { key: "purchaseFinancialInvestments", label: "Investime financiare dhe pjesëmarrje" },
        { key: "netInvesting", label: "Neto rrjedha nga aktivitetet investuese", isTotal: true },
      ],
    },
    {
      title: "RRJEDHA NGA AKTIVITETET FINANCUESE",
      rows: [
        { key: "changeShortTermLoans", label: "Ndryshimi në kreditë afatshkurtra" },
        { key: "changeLongTermLoans", label: "Ndryshimi në kreditë afatgjata" },
        { key: "changeLeaseLiabilities", label: "Ndryshimi në detyrimet e lizingut" },
        { key: "changeOwnerCapital", label: "Ndryshimi në kapitalin e pronarit" },
        { key: "changeReserves", label: "Ndryshimi në rezerva" },
        { key: "changeRetainedEarnings", label: "Ndryshimi në fitimin e bartur" },
        { key: "changeBankOverdraft", label: "Ndryshimi në mbitërheqjen bankare" },
        { key: "reconciliationAdjustment", label: "Rregullim për barazim me ndryshimin e parasë" },
        { key: "netFinancing", label: "Neto rrjedha nga aktivitetet financuese", isTotal: true },
      ],
    },
    {
      title: "PARAJA DHE EKUIVALENTËT",
      rows: [
        { key: "openingCash", label: "Paraja në fillim të periudhës" },
        { key: "netCashMovement", label: "Rritja / (ulja) neto e parasë gjatë periudhës", isTotal: true },
        { key: "closingCash", label: "Paraja në fund të periudhës", isTotal: true },
      ],
    },
  ];

  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const priorFiscalYear = Number(fiscalYear) - 1;
    const periodDate = `31.12.${fiscalYear}`;
    const current = data?.currentFlow ?? {};
    const prior = data?.priorFlow ?? {};

    const bodyRows = [];
    const sectionRowIndexes = new Set();
    const totalRowIndexes = new Set();
    let rowIndex = 0;

    cfSections.forEach((section) => {
      bodyRows.push([{ content: section.title, colSpan: 3 }]);
      sectionRowIndexes.add(rowIndex);
      rowIndex += 1;

      section.rows.forEach((row) => {
        bodyRows.push([
          row.label || "",
          formatNumber(current[row.key]),
          formatNumber(prior[row.key]),
        ]);
        if (row.isTotal) {
          totalRowIndexes.add(rowIndex);
        }
        rowIndex += 1;
      });
    });

    doc.setTextColor(25, 25, 25);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(companyInfo?.CompanyName || "Kompania", 105, 18.5, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`Pasqyra e rrjedhës së parasë për vitin ${fiscalYear}`, 105, 23.5, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Statement of Cash Flows", 105, 27.5, { align: "center" });

    doc.setFontSize(8);
    doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 32.5);
    doc.text(`Periudha: ${periodDate}`, 200, 32.5, { align: "right" });

    autoTable(doc, {
      startY: 38,
      head: [["Përshkrimi", `31.12.${fiscalYear}`, `31.12.${priorFiscalYear}`]],
      body: bodyRows,
      theme: "grid",
      styles: {
        fontSize: 7.4,
        cellPadding: 0.95,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
        fontStyle: "bold",
        lineColor: [40, 73, 69],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { cellWidth: 126 },
        1: { cellWidth: 36, halign: "right" },
        2: { cellWidth: 36, halign: "right" },
      },
      didParseCell: (hookData) => {
        const currentRowIndex = hookData.row.index;
        if (sectionRowIndexes.has(currentRowIndex)) {
          hookData.cell.styles.fillColor = [233, 241, 237];
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.halign = "left";
          hookData.cell.styles.textColor = [31, 58, 54];
        }
        if (totalRowIndexes.has(currentRowIndex)) {
          hookData.cell.styles.fillColor = [245, 249, 247];
          hookData.cell.styles.fontStyle = "bold";
        }
      },
      margin: { top: 8, right: 6, bottom: 8, left: 6 },
      pageBreak: "auto",
    });

    let signatureY = (doc.lastAutoTable?.finalY || 40) + 20;
    const pageHeight = doc.internal.pageSize.getHeight();

    if (signatureY > pageHeight - 22) {
      doc.addPage();
      signatureY = 36;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text("Pergatiti", 42, signatureY - 7, { align: "center" });
    doc.text("Miratoi", 148, signatureY - 7, { align: "center" });
    doc.line(20, signatureY, 85, signatureY);
    doc.line(115, signatureY, 180, signatureY);

    const totalPages = doc.internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
      doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
    }

    const companySlug = (companyInfo?.CompanyName || "Kompania")
      .trim()
      .replace(/\s+/g, "_");

    return { doc, companySlug };
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || isLoading || !data) return;
    setIsGeneratingPdf(true);
    try {
      const { doc, companySlug } = buildPdfDoc();
      doc.save(`Pasqyra_e_rrjedhes_se_parase_${fiscalYear}_${companySlug}.pdf`);
    } catch (pdfError) {
      console.error("Gabim gjatë gjenerimit të PDF:", pdfError);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = async () => {
    if (isGeneratingPrint || isLoading || !data) return;
    setIsGeneratingPrint(true);
    try {
      const { doc } = buildPdfDoc();
      const blobUrl = doc.output("bloburl");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (printError) {
      console.error("Gabim gjatë printimit:", printError);
    } finally {
      setIsGeneratingPrint(false);
    }
  };

  useEffect(() => {
    const fetchCashFlowData = async () => {
      if (!fiscalYear || fiscalYear <= 0) {
        setError("Viti fiskal nuk është përcaktuar saktë.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const headers = authHeader();
        const year = Number(fiscalYear);

        const [bsNow, isNow, bsPrev, isPrev, companyRes] = await Promise.all([
          axios.get(ApiLink.API_URL + "BalanceSheet/getBalanceSheet", {
            headers,
            params: { fiscalYear: year },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear: year },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getBalanceSheet", {
            headers,
            params: { fiscalYear: year - 1 },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear: year - 1 },
          }),
          axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers }),
        ]);

        if (Array.isArray(companyRes.data) && companyRes.data.length > 0) {
          setCompanyInfo(companyRes.data[0]);
        }

        const normalizedBsNow = normalizeKeys(bsNow.data);
        const normalizedIsNow = normalizeKeys(isNow.data);
        const normalizedBsPrev = normalizeKeys(bsPrev.data);
        const normalizedIsPrev = normalizeKeys(isPrev.data);

        const currentFlow = computeCashFlow(
          normalizedBsNow?.currentYear,
          normalizedBsNow?.priorYear,
          normalizedIsNow?.currentYear
        );

        const priorFlow = computeCashFlow(
          normalizedBsPrev?.currentYear,
          normalizedBsPrev?.priorYear,
          normalizedIsPrev?.currentYear
        );

        setData({ currentFlow, priorFlow });
      } catch (fetchError) {
        console.error("Gabim në marrjen e pasqyrës së rrjedhës së parasë:", fetchError);
        setError(
          fetchError.response?.data?.message || "Gabim në marrjen e të dhënave"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCashFlowData();
  }, [fiscalYear]);

  if (error) {
    return (
      <div className="CF__Page__Container">
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            color: "#856404",
          }}
        >
          <h3>Gabim</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="CF__Page__Container">
      <div className="CF__Page__HeaderData">
        <CompanyHeader
          reportTitle={`Pasqyra e rrjedhës së parasë për vitin që përfundon më 31 dhjetor ${fiscalYear}`}
          reportSubtitle="Statement of Cash Flows"
          fiscalYear={fiscalYear}
          onDownloadPdf={handleDownloadPdf}
          isGeneratingPdf={isGeneratingPdf}
          onPrint={handlePrintPdf}
          isGeneratingPrint={isGeneratingPrint}
        />
      </div>

      <hr className="CF__Page__Divider" />

      <div className="CF__Page__TableWrap">
        <CashFlowTable
          currentFlow={data?.currentFlow}
          priorFlow={data?.priorFlow}
          fiscalYear={Number(fiscalYear)}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
